/**
 * @file routes/(public)/login.tsx
 * @description 登入頁面路由（公開，無需認證）。
 *
 * 設計：左右分屏 — 深色品牌展示區 + 白色登入表單
 * 風格：企業精品級，動態幾何盾牌圖案，精緻入場動畫
 *
 * 功能：
 * - 帳號密碼登入表單
 * - 快速體驗：4 個內部角色一鍵填入表單並提交
 * - 登入成功後更新全局 AuthStore 並跳轉至 /dashboard
 * - 提供「忘記密碼」連結
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Form, Input, Button, Typography } from 'antd';
import { message, modal } from '@/lib/antd-app';
import {
  User,
  Lock,
  ArrowRight,
  Zap,
  ChevronRight,
  Settings,
  Search,
  Layers,
  Stamp,
} from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { useAuthStore, type AuthUser } from '@/lib/auth-context';
import { VersionBadge } from '@/components/common/VersionBadge';

const { Title, Text } = Typography;

/** 路由定義 */
export const Route = createFileRoute('/(public)/login')({ component: LoginPage });

/**
 * 快速登入角色配置（4 個內部角色 — Demo 用途）。
 *
 * 密碼：4 個角色統一使用 Demo 密碼 `asdfasdfasdf`，需與資料庫中對應帳號的密碼一致。
 */
const QUICK_LOGIN_ROLES = [
  {
    label: '系統管理員',
    username: 'admin',
    password: 'asdfasdfasdf',
    icon: <Settings size={18} color="#1a8549" />,
    color: '#1a8549',
  },
  {
    label: '管理組',
    username: 'manager01',
    password: 'asdfasdfasdf',
    icon: <Layers size={18} color="#156939" />,
    color: '#156939',
  },
  {
    label: '驗證決定組',
    username: 'decider01',
    password: 'asdfasdfasdf',
    icon: <Stamp size={18} color="#0d3820" />,
    color: '#0d3820',
  },
  {
    label: '稽核員',
    username: 'auditor01',
    password: 'asdfasdfasdf',
    icon: <Search size={18} color="#27a05a" />,
    color: '#27a05a',
  },
] as const;

/**
 * 登入頁面。
 * 採用左右分屏設計：左側為動態幾何品牌展示區，右側為白色表單區。
 */
function LoginPage() {
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [form] = Form.useForm();

  /* 入場動畫觸發 */
  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);

  /**
   * 處理登入 — 調用 POST /api/auth/login，成功後更新全局 AuthStore 並導航。
   */
  const handleLogin = async (values: { username: string; password: string }) => {
    setLoading(true);
    try {
      const r = await apiClient.post<{ user: AuthUser; passwordIsInitial?: boolean }>(
        '/api/auth/login',
        values,
      );
      setUser(r.user);
      message.success(`歡迎回來，${r.user.name}！`);
      if (r.passwordIsInitial) {
        // 序51：以系統預設初始密碼登入 → 提示盡速修改（不阻擋登入，可稍後再改）
        void navigate({ to: '/me/profile' });
        modal.warning({
          title: '請修改初始密碼',
          content:
            '您目前使用系統預設的初始密碼登入，為保障帳號安全，請在「個人設定 → 重置密碼」修改為 6–10 個字元、英文與數字混合的新密碼。',
          okText: '我知道了',
        });
      } else {
        void navigate({ to: '/dashboard' });
      }
    } catch (e) {
      message.error(e instanceof Error ? e.message : '登入失敗');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 快速登入（Demo 用途）：按角色填入帳號與對應密碼後自動提交表單。
   * 每個角色各自的 seed 密碼見 QUICK_LOGIN_ROLES 註釋。
   */
  const handleQuickLogin = (username: string, password: string) => {
    form.setFieldsValue({ username, password });
    form.submit();
  };

  return (
    <>
      {/* 內嵌動畫樣式 */}
      <style>{loginAnimations}</style>

      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          background: '#0d3820',
        }}
      >
        {/* ====== 左側品牌展示區 ====== */}
        <div
          style={{
            flex: '0 0 52%',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '60px 48px',
            background:
              'linear-gradient(160deg, #0d3820 0%, #114f2c 40%, #156939 70%, #1a8549 100%)',
          }}
          className="login-left-panel"
        >
          {/* 背景網格線條 */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `
                linear-gradient(rgba(39, 160, 90, 0.04) 1px, transparent 1px),
                linear-gradient(90deg, rgba(39, 160, 90, 0.04) 1px, transparent 1px)
              `,
              backgroundSize: '60px 60px',
              pointerEvents: 'none',
            }}
          />

          {/* 動態光暈效果 */}
          <div
            style={{
              position: 'absolute',
              top: '15%',
              left: '20%',
              width: 400,
              height: 400,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(39, 160, 90, 0.12) 0%, transparent 70%)',
              animation: 'loginFloat 8s ease-in-out infinite',
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: '10%',
              right: '15%',
              width: 300,
              height: 300,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(26, 133, 73, 0.1) 0%, transparent 70%)',
              animation: 'loginFloat 10s ease-in-out infinite reverse',
              pointerEvents: 'none',
            }}
          />

          {/* 幾何裝飾線條 */}
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              opacity: mounted ? 0.3 : 0,
              transition: 'opacity 1.5s ease 0.3s',
            }}
            viewBox="0 0 600 800"
            preserveAspectRatio="xMidYMid slice"
          >
            {/* 旋轉六邊形 */}
            <polygon
              points="300,120 380,166 380,258 300,304 220,258 220,166"
              fill="none"
              stroke="rgba(39, 160, 90, 0.2)"
              strokeWidth="1"
              style={{ animation: 'loginRotate 30s linear infinite' }}
              // @ts-expect-error SVG transform-origin on inline style is valid in browsers
              transformOrigin="300 212"
            />
            <polygon
              points="300,150 360,182 360,246 300,278 240,246 240,182"
              fill="none"
              stroke="rgba(26, 133, 73, 0.15)"
              strokeWidth="0.5"
              style={{
                animation: 'loginRotate 25s linear infinite reverse',
              }}
              // @ts-expect-error SVG transform-origin on inline style is valid in browsers
              transformOrigin="300 214"
            />
            {/* 裝飾點陣 */}
            {[
              [80, 200],
              [520, 150],
              [150, 600],
              [450, 650],
              [100, 400],
              [500, 450],
              [250, 700],
              [400, 100],
            ].map(([cx, cy], i) => (
              <circle
                key={i}
                cx={cx}
                cy={cy}
                r={1.5}
                fill="rgba(39, 160, 90, 0.3)"
                style={{
                  animation: `loginPulse ${3 + i * 0.5}s ease-in-out infinite`,
                  animationDelay: `${i * 0.3}s`,
                }}
              />
            ))}
            {/* 連線 */}
            <line
              x1="80"
              y1="200"
              x2="220"
              y2="166"
              stroke="rgba(39, 160, 90, 0.08)"
              strokeWidth="0.5"
            />
            <line
              x1="520"
              y1="150"
              x2="380"
              y2="166"
              stroke="rgba(39, 160, 90, 0.08)"
              strokeWidth="0.5"
            />
            <line
              x1="150"
              y1="600"
              x2="250"
              y2="700"
              stroke="rgba(39, 160, 90, 0.06)"
              strokeWidth="0.5"
            />
          </svg>

          {/* 中央盾牌 Logo */}
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              textAlign: 'center',
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.2s',
            }}
          >
            {/* 盾牌圖標容器 */}
            <div
              style={{
                width: 96,
                height: 96,
                margin: '0 auto 32px',
                borderRadius: 28,
                background:
                  'linear-gradient(135deg, rgba(39, 160, 90, 0.2) 0%, rgba(26, 133, 73, 0.1) 100%)',
                border: '1px solid rgba(39, 160, 90, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(20px)',
                position: 'relative',
              }}
            >
              {/* 外環光暈 */}
              <div
                style={{
                  position: 'absolute',
                  inset: -2,
                  borderRadius: 30,
                  background:
                    'linear-gradient(135deg, rgba(39, 160, 90, 0.3), transparent, rgba(26, 133, 73, 0.2))',
                  opacity: 0.6,
                  animation: 'loginRotate 8s linear infinite',
                  zIndex: -1,
                }}
              />
              {/* 品牌 logo（深色背景下用 invert 反相為白） */}
              <img
                src="/zhcert-logo-icon.png"
                alt="ZHCERT"
                style={{
                  width: 56,
                  height: 56,
                  objectFit: 'contain',
                  filter: 'invert(1)',
                  opacity: 0.92,
                }}
              />
            </div>

            {/* 品牌名稱 */}
            <h1
              style={{
                fontSize: 32,
                fontWeight: 700,
                color: '#e6f4ed',
                margin: '0 0 12px',
                letterSpacing: '0.02em',
                lineHeight: 1.3,
              }}
            >
              ZHCB 驗證管理系統
            </h1>
            <p
              style={{
                fontSize: 15,
                color: 'rgba(167, 220, 189, 0.6)',
                margin: '0 0 48px',
                letterSpacing: '0.04em',
                lineHeight: 1.6,
              }}
            >
              基於 ISO/IEC 17065 標準
              <br />
              驗證機構管理平台
            </p>

            {/* 特性標籤 */}
            <div
              style={{
                display: 'flex',
                gap: 12,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              {['稽核管理', '證書追蹤', '合規保障'].map((tag, i) => (
                <span
                  key={tag}
                  style={{
                    padding: '8px 18px',
                    borderRadius: 100,
                    fontSize: 13,
                    color: 'rgba(167, 220, 189, 0.7)',
                    background: 'rgba(39, 160, 90, 0.08)',
                    border: '1px solid rgba(39, 160, 90, 0.12)',
                    backdropFilter: 'blur(8px)',
                    opacity: mounted ? 1 : 0,
                    transform: mounted ? 'translateY(0)' : 'translateY(15px)',
                    transition: `all 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${0.6 + i * 0.1}s`,
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* 底部版權 */}
          <div
            style={{
              position: 'absolute',
              bottom: 32,
              left: 0,
              right: 0,
              textAlign: 'center',
              opacity: mounted ? 0.4 : 0,
              transition: 'opacity 1s ease 1s',
            }}
          >
            <span
              style={{
                fontSize: 12,
                color: 'rgba(167, 220, 189, 0.5)',
                letterSpacing: '0.05em',
              }}
            >
              © 2026 ZHCB Certification Body
            </span>
            {/* 版本徽標（前後端版本比對，確認部署是否生效） */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 6 }}>
              <VersionBadge dark />
            </div>
          </div>
        </div>

        {/* ====== 右側表單區 ====== */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '48px 40px',
            background: '#ffffff',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* 背景紋理 */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage:
                'radial-gradient(circle at 80% 20%, rgba(39, 160, 90, 0.03) 0%, transparent 50%)',
              pointerEvents: 'none',
            }}
          />

          {/* 表單容器 */}
          <div
            style={{
              width: '100%',
              maxWidth: 380,
              position: 'relative',
              zIndex: 1,
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateX(0)' : 'translateX(20px)',
              transition: 'all 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.4s',
            }}
          >
            {/* 歡迎標題 */}
            <div style={{ marginBottom: 40 }}>
              <Text
                style={{
                  display: 'block',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#27a05a',
                  textTransform: 'uppercase' as const,
                  letterSpacing: '0.1em',
                  marginBottom: 12,
                }}
              >
                Welcome back
              </Text>
              <Title
                level={2}
                style={{
                  margin: '0 0 8px',
                  fontSize: 28,
                  fontWeight: 700,
                  color: '#0f172a',
                  letterSpacing: '-0.02em',
                }}
              >
                登入您的帳號
              </Title>
              <Text
                style={{
                  fontSize: 14,
                  color: '#94a3b8',
                  lineHeight: 1.6,
                }}
              >
                請輸入您的帳號資訊以存取系統
              </Text>
            </div>

            {/* 登入表單 */}
            <Form
              form={form}
              layout="vertical"
              onFinish={handleLogin}
              size="large"
              requiredMark={false}
            >
              <Form.Item
                name="username"
                label={
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#475569',
                    }}
                  >
                    用戶名
                  </span>
                }
                rules={[{ required: true, message: '請輸入用戶名' }]}
                style={{ marginBottom: 20 }}
              >
                <Input
                  prefix={<User size={16} color="#94a3b8" style={{ marginRight: 4 }} />}
                  placeholder="請輸入用戶名"
                  style={{
                    borderRadius: 12,
                    height: 48,
                    borderColor: '#e2e8f0',
                    fontSize: 14,
                  }}
                />
              </Form.Item>

              <Form.Item
                name="password"
                label={
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: '#475569',
                      }}
                    >
                      密碼
                    </span>
                    <a
                      href="/forgot-password"
                      style={{
                        fontSize: 13,
                        color: '#27a05a',
                        fontWeight: 500,
                        textDecoration: 'none',
                        marginLeft: 16,
                      }}
                    >
                      忘記密碼？
                    </a>
                  </div>
                }
                rules={[{ required: true, message: '請輸入密碼' }]}
                style={{ marginBottom: 28 }}
              >
                <Input.Password
                  prefix={<Lock size={16} color="#94a3b8" style={{ marginRight: 4 }} />}
                  placeholder="請輸入密碼"
                  style={{
                    borderRadius: 12,
                    height: 48,
                    borderColor: '#e2e8f0',
                    fontSize: 14,
                  }}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  style={{
                    height: 48,
                    fontSize: 15,
                    fontWeight: 600,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #156939 0%, #27a05a 100%)',
                    border: 'none',
                    boxShadow:
                      '0 4px 14px rgba(39, 160, 90, 0.3), 0 1px 3px rgba(39, 160, 90, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                  icon={!loading ? <ArrowRight size={18} /> : undefined}
                  iconPlacement="end"
                >
                  登入系統
                </Button>
              </Form.Item>
            </Form>

            {/* 快速體驗分隔 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                margin: '32px 0 24px',
              }}
            >
              <div style={{ flex: 1, height: 1, background: '#f1f5f9' }} />
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Zap size={12} color="#cbd5e1" />
                <span
                  style={{
                    fontSize: 12,
                    color: '#94a3b8',
                    fontWeight: 500,
                    letterSpacing: '0.04em',
                  }}
                >
                  快速體驗
                </span>
              </div>
              <div style={{ flex: 1, height: 1, background: '#f1f5f9' }} />
            </div>

            {/* 快速登入角色按鈕 */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 10,
              }}
            >
              {QUICK_LOGIN_ROLES.map((item, i) => (
                <button
                  key={item.username}
                  onClick={() => handleQuickLogin(item.username, item.password)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '12px 14px',
                    borderRadius: 12,
                    border: '1px solid #f1f5f9',
                    background: '#fafbfc',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    // 入场动画用 CSS @keyframes 而非 transition，避免 transitionDelay 拖慢 hover 反应
                    animation: mounted ? `quickLoginIn 0.4s ease ${0.7 + i * 0.08}s both` : 'none',
                    opacity: mounted ? undefined : 0,
                  }}
                  onMouseEnter={(e) => {
                    const el = e.currentTarget;
                    el.style.borderColor = item.color;
                    el.style.background = '#ffffff';
                    el.style.boxShadow = `0 2px 12px ${item.color}15`;
                  }}
                  onMouseLeave={(e) => {
                    const el = e.currentTarget;
                    el.style.borderColor = '#f1f5f9';
                    el.style.background = '#fafbfc';
                    el.style.boxShadow = 'none';
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 18,
                      height: 18,
                    }}
                  >
                    {item.icon}
                  </span>
                  <div
                    style={{
                      flex: 1,
                      textAlign: 'left',
                    }}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#334155',
                        lineHeight: 1.3,
                      }}
                    >
                      {item.label}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: '#94a3b8',
                        lineHeight: 1.3,
                      }}
                    >
                      {item.username}
                    </div>
                  </div>
                  <ChevronRight size={14} color="#cbd5e1" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/** 登入頁動畫關鍵幀 — 浮動、旋轉、脈動 */
const loginAnimations = `
  @keyframes loginFloat {
    0%, 100% { transform: translate(0, 0); }
    33% { transform: translate(15px, -20px); }
    66% { transform: translate(-10px, 10px); }
  }

  @keyframes loginRotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @keyframes loginPulse {
    0%, 100% { opacity: 0.3; r: 1.5; }
    50% { opacity: 0.8; r: 2.5; }
  }

  /* 快速登入卡片入場（stagger） */
  @keyframes quickLoginIn {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* 響應式：平板以下隱藏左側面板 */
  @media (max-width: 860px) {
    .login-left-panel {
      display: none !important;
    }
  }

  /* 登入按鈕 hover 效果 */
  .ant-btn-primary:hover {
    box-shadow: 0 6px 20px rgba(39, 160, 90, 0.4), 0 2px 6px rgba(39, 160, 90, 0.25) !important;
    transform: translateY(-1px);
  }

  .ant-btn-primary:active {
    transform: translateY(0);
  }

  /* 輸入框 focus 效果增強 */
  .ant-input:focus,
  .ant-input-affix-wrapper:focus,
  .ant-input-affix-wrapper-focused {
    border-color: #27a05a !important;
    box-shadow: 0 0 0 3px rgba(39, 160, 90, 0.08) !important;
  }
`;
